import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
	generatePattern,
	generateSamplePattern,
} from '../services/pdf.service';
import { getConfigValue } from '../services/remoteConfig.service';
import { fetchPatternByFileName } from '../services/library.service';
import {
	trackError,
	trackPatternMakerStep,
	ANALYTICS_EVENTS,
	ERROR_CONTEXTS,
} from '../services/analytics.service';
import {
	ANGLE_LINES,
	FIREBASE_STORAGE_PATTERN_LIBRARY_URL,
	IMAGETYPE,
	MEASUREMENT_UNITS,
} from '../config/constants';

// Encapsulates all pattern wizard state. Mirrors PatternProvider but goes through
// the service layer (pdf.service, remoteConfig.service, library.service) instead
// of importing Firebase and PatternBuilder directly.
export const usePattern = () => {
	const { Jimp } = window;
	const [imagePrimaryDataURL, setImagePrimaryDataURL] = useState(null);
	const [imageCleanPrimaryDataURL, setImageCleanPrimaryDataURL] =
		useState(null);
	const [imagePrimaryJimpError, setImagePrimaryJimpError] = useState(null);
	const [imagePrimaryMetadata, setImagePrimaryMetadata] = useState(null);

	const [imageSecondaryDataURL, setImageSecondaryDataURL] = useState(null);
	const [imageCleanSecondaryDataURL, setImageCleanSecondaryDataURL] =
		useState(null);
	const [imageSecondaryJimpError, setImageSecondaryJimpError] =
		useState(null);
	const [imageSecondaryMetadata, setImageSecondaryMetadata] = useState(null);

	const [imageType, setImageType] = useState(IMAGETYPE.SINGLE);
	const [lastOddPage, setLastOddPage] = useState(249);
	const [height, setHeight] = useState(220);
	const [angleLine, setAngleLine] = useState(ANGLE_LINES.NONE);
	const [isInkSaver, setIsInkSaver] = useState(false);
	const [measurementUnit, setMeasurementUnit] = useState(
		MEASUREMENT_UNITS[0].value,
	);
	const [extraFrontPages, setExtraFrontPages] = useState(0);
	const [extraBackPages, setExtraBackPages] = useState(0);
	const [libraryPatternInfo, setLibraryPatternInfo] = useState({});

	const [searchParams, setSearchParams] = useSearchParams();

	// Load library images from URL params (?a=fileName&b=fileName)
	useEffect(() => {
		const fetchParams = async () => {
			try {
				const a = searchParams.get('a');
				const b = searchParams.get('b');

				const baseUrl = FIREBASE_STORAGE_PATTERN_LIBRARY_URL;

				const fetchImage = async (param) => {
					const watermarkedUrl = `${baseUrl}${param}.jpg`;
					const cleanUrl = `${baseUrl}${param}-c.jpg`;
					const [watermarkedRes, cleanRes] = await Promise.all([
						fetch(watermarkedUrl),
						fetch(cleanUrl),
					]);
					if (!watermarkedRes.ok || !cleanRes.ok) return null;
					const [watermarkedInfo, cleanInfo] = await Promise.all([
						watermarkedRes.json(),
						cleanRes.json(),
					]);
					return {
						url: `${watermarkedUrl}?alt=media&token=${watermarkedInfo.downloadTokens}`,
						cleanUrl: `${cleanUrl}?alt=media&token=${cleanInfo.downloadTokens}`,
					};
				};

				if (a) {
					const result = await fetchImage(a);
					if (!result) {
						setSearchParams({});
						return;
					}
					setImagePrimaryDataURL(result.url);
					setImageCleanPrimaryDataURL(result.cleanUrl);
					setImagePrimaryMetadata(a);
					trackPatternMakerStep(
						ANALYTICS_EVENTS.PATTERN_MAKER_LIBRARY_IMAGE_LOADED,
						{ has_secondary: !!b },
					);
				}

				if (b) {
					const result = await fetchImage(b);
					if (!result) {
						setSearchParams({});
						return;
					}
					setImageSecondaryDataURL(result.url);
					setImageCleanSecondaryDataURL(result.cleanUrl);
					setImageSecondaryMetadata(b);
					setImageType(IMAGETYPE.DOUBLE);
				}
			} catch (error) {
				trackError(ERROR_CONTEXTS.FETCH_PARAMS, error);
				setSearchParams({});
			}
		};

		fetchParams();
	}, [searchParams, setSearchParams]);

	// Jimp validation for primary image
	useEffect(() => {
		if (imagePrimaryDataURL === null) return;
		Jimp.read(imagePrimaryDataURL)
			.then(() => setImagePrimaryJimpError(null))
			.catch((error) => {
				setImagePrimaryJimpError(error.message);
				trackPatternMakerStep(
					ANALYTICS_EVENTS.PATTERN_MAKER_IMAGE_ERROR,
					{ image: 'primary', error_message: error.message },
				);
			});
	}, [Jimp, imagePrimaryDataURL]);

	// Jimp validation for secondary image
	useEffect(() => {
		if (imageSecondaryDataURL === null) return;
		Jimp.read(imageSecondaryDataURL)
			.then(() => setImageSecondaryJimpError(null))
			.catch((error) => {
				setImageSecondaryJimpError(error.message);
				trackPatternMakerStep(
					ANALYTICS_EVENTS.PATTERN_MAKER_IMAGE_ERROR,
					{ image: 'secondary', error_message: error.message },
				);
			});
	}, [Jimp, imageSecondaryDataURL]);

	// Fetch library pattern metadata when a library image is loaded
	useEffect(() => {
		const meta = imagePrimaryMetadata ?? imageSecondaryMetadata;
		if (!meta) return;
		fetchPatternByFileName(meta).then((info) => {
			if (info) setLibraryPatternInfo(info);
		});
	}, [imagePrimaryMetadata, imageSecondaryMetadata]);

	const setImagePrimaryDataURLAndParams = (value) => {
		if (value) {
			trackPatternMakerStep(
				ANALYTICS_EVENTS.PATTERN_MAKER_IMAGE_UPLOADED,
				{ has_secondary_image: !!imageSecondaryDataURL },
			);
		}
		setImagePrimaryDataURL(value);
		setImageCleanPrimaryDataURL(null);
		setImagePrimaryMetadata(null);
		setSearchParams(
			(prev) => {
				prev.delete('a');
				return prev;
			},
			{ replace: true },
		);
	};

	const setImageSecondaryDataURLAndParams = (value) => {
		setImageSecondaryDataURL(value);
		setImageCleanSecondaryDataURL(null);
		setImageSecondaryMetadata(null);
		setSearchParams(
			(prev) => {
				prev.delete('b');
				return prev;
			},
			{ replace: true },
		);
	};

	const setImageTypeAndParams = (value) => {
		setImageType(value);
		if (value === IMAGETYPE.SINGLE) {
			setImageSecondaryDataURL(null);
			setImageSecondaryJimpError(null);
			setSearchParams(
				(prev) => {
					prev.delete('b');
					return prev;
				},
				{ replace: true },
			);
		}
	};

	const getPattern = async () => {
		const pdfPassword = getConfigValue('PDF_PASSWORD');
		const imagePrimaryInput =
			imageCleanPrimaryDataURL ?? imagePrimaryDataURL;
		const imageSecondaryInput =
			imageType === IMAGETYPE.DOUBLE
				? (imageCleanSecondaryDataURL ?? imageSecondaryDataURL)
				: null;

		return generatePattern({
			angleLine,
			isInkSaver,
			imagePrimaryDataURL: imagePrimaryInput,
			imageSecondaryDataURL: imageSecondaryInput,
			height,
			lastOddPage,
			measurementUnit,
			extraFrontPages,
			extraBackPages,
			pdfPassword,
		});
	};

	const getSamplePattern = async () => {
		const imageSecondaryInput =
			imageType === IMAGETYPE.DOUBLE ? imageSecondaryDataURL : null;

		const result = await generateSamplePattern({
			angleLine,
			isInkSaver,
			imagePrimaryDataURL,
			imageSecondaryDataURL: imageSecondaryInput,
			height,
			lastOddPage,
			measurementUnit,
			extraFrontPages,
			extraBackPages,
		});
		trackPatternMakerStep(ANALYTICS_EVENTS.PATTERN_MAKER_SAMPLE_GENERATED);
		return result;
	};

	return {
		imagePrimary: {
			dataURL: imagePrimaryDataURL,
			setDataURL: setImagePrimaryDataURLAndParams,
			jimpError: imagePrimaryJimpError,
			metadata: imagePrimaryMetadata,
		},
		imageSecondary: {
			dataURL: imageSecondaryDataURL,
			setDataURL: setImageSecondaryDataURLAndParams,
			jimpError: imageSecondaryJimpError,
			metadata: imageSecondaryMetadata,
		},
		libraryPatternInfo: {
			value: libraryPatternInfo,
			setValue: setLibraryPatternInfo,
		},
		imageType: { value: imageType, setValue: setImageTypeAndParams },
		lastOddPage: { value: lastOddPage, setValue: setLastOddPage },
		height: { value: height, setValue: setHeight },
		angleLine: { value: angleLine, setValue: setAngleLine },
		isInkSaver: { value: isInkSaver, setValue: setIsInkSaver },
		measurementUnit: {
			value: measurementUnit,
			setValue: setMeasurementUnit,
		},
		extraFrontPages: {
			value: extraFrontPages,
			setValue: setExtraFrontPages,
		},
		extraBackPages: { value: extraBackPages, setValue: setExtraBackPages },
		getPattern,
		getSamplePattern,
	};
};
